import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Heart, Eye, Star, Send, Sparkles, TrendingUp, CheckCircle2, ArrowRight, UserCheck, Crown, ShieldCheck,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MatchCard } from "@/components/MatchCard";
import { ProfileNudges } from "@/components/ProfileNudges";
import { FullSpinner, SkeletonCard, Empty } from "@/components/States";

const JOURNEY = ["Profile Created", "Profile Completed", "Verified", "Match Found", "Interest", "Accepted", "Chat", "Contact", "Meeting"];

export default function Dashboard() {
  const { profile, user } = useAuth();
  const [data, setData] = useState(null);
  const [feed, setFeed] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => setData({ stats: {}, todays_matches: [], tips: [] }));
    api.get("/home-feed").then(({ data }) => setFeed(data.sections)).catch(() => setFeed([]));
  }, []);

  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good Morning" : hour < 17 ? "Good Afternoon" : "Good Evening";
  const first = (profile?.full_name || "there").split(" ")[0];
  const completion = data?.stats?.completion ?? profile?.completion ?? 0;
  const journeyStep = Math.max(1, Math.min(JOURNEY.length, Math.round((completion / 100) * 3) + ((data?.stats?.interests_sent || 0) > 0 ? 1 : 0)));

  if (!data) return <FullSpinner />;

  const cards = [
    { label: "New Matches", value: data.stats.new_matches, icon: Heart, to: "/app/matches" },
    { label: "Interests Received", value: data.stats.interests_received, icon: UserCheck, to: "/app/interests" },
    { label: "Interests Sent", value: data.stats.interests_sent, icon: Send, to: "/app/interests" },
    { label: "Profile Views", value: data.stats.profile_views, icon: Eye, to: "/app/matches/who_viewed_me" },
    { label: "Shortlists", value: data.stats.shortlists, icon: Star, to: "/app/shortlist" },
    { label: "Pending Requests", value: data.stats.pending_requests, icon: TrendingUp, to: "/app/requests" },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#58111A]">{greet}, {first}</h1>
          <p className="text-sm text-[#605255]">Here's what's happening on your journey today.</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {user?.subscription?.plan === "free" ? (
            <Link to="/app/subscription" className="chip"><Crown className="w-3.5 h-3.5" /> Free Plan · Upgrade</Link>
          ) : (
            <span className="chip"><Crown className="w-3.5 h-3.5" /> {user.subscription.lifetime ? "Lifetime" : "Premium"} Member</span>
          )}
        </div>
      </div>

      {/* Profile completion */}
      <div className="mj-card p-6" data-testid="profile-completion-card">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-serif text-xl font-semibold text-[#261A1C]">Complete Your Profile</h2>
          <span className="font-serif text-2xl font-bold text-[#781826]">{completion}%</span>
        </div>
        <div className="h-2.5 rounded-full bg-[#F1E9DA] overflow-hidden mb-4">
          <div className="h-full rounded-full transition-all" style={{ width: `${completion}%`, background: "linear-gradient(90deg,#C59B27,#781826)" }} />
        </div>
        {data.tips?.length > 0 ? (
          <div className="grid sm:grid-cols-2 gap-2">
            {data.tips.slice(0, 4).map((t) => (
              <Link key={t} to="/app/profile/edit" className="flex items-center gap-2 text-sm text-[#605255] hover:text-[#781826] rounded-lg px-3 py-2 bg-[#FAF5EE]">
                <ArrowRight className="w-3.5 h-3.5 text-[#C59B27]" /> {t}
              </Link>
            ))}
          </div>
        ) : completion >= 80 ? (
          <p className="text-sm text-[#15803D] flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Your profile looks great!</p>
        ) : (
          <Link to="/app/profile/edit" className="text-sm text-[#781826] font-medium flex items-center gap-2 hover:underline"><ArrowRight className="w-4 h-4 text-[#C59B27]" /> Let's build your profile to unlock better matches</Link>
        )}
      </div>

      {/* Gentle profile nudges */}
      <ProfileNudges />

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {cards.map(({ label, value, icon: Icon, to }) => (
          <Link key={label} to={to} className="mj-card p-4 hover:border-[#C59B27]/50 transition" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <Icon className="w-5 h-5 text-[#C59B27] mb-2" />
            <div className="font-serif text-2xl font-bold text-[#58111A]">{value ?? 0}</div>
            <div className="text-xs text-[#605255]">{label}</div>
          </Link>
        ))}
      </div>

      {/* Match journey */}
      <div className="mj-card p-6" data-testid="match-journey">
        <h2 className="font-serif text-xl font-semibold text-[#261A1C] mb-4">Your Match Journey</h2>
        <div className="flex items-center gap-1 overflow-x-auto hide-scrollbar pb-1">
          {JOURNEY.map((s, i) => (
            <div key={s} className="flex items-center shrink-0">
              <div className={`flex flex-col items-center gap-1 px-2 ${i < journeyStep ? "text-[#781826]" : "text-slate-400"}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i < journeyStep ? "bg-[#781826] text-white" : "bg-[#F1E9DA] text-slate-400"}`}>{i + 1}</div>
                <span className="text-[10px] whitespace-nowrap font-medium">{s}</span>
              </div>
              {i < JOURNEY.length - 1 && <div className={`w-6 h-0.5 ${i < journeyStep - 1 ? "bg-[#C59B27]" : "bg-[#F1E9DA]"}`} />}
            </div>
          ))}
        </div>
        <p className="text-xs text-[#9E7B1B] mt-3">This is a progress tracker only and does not imply any guaranteed outcome.</p>
      </div>

      {/* Personalized Home Feed */}
      {feed && feed.length > 0 ? (
        <div className="space-y-7" data-testid="home-feed">
          {feed.map((section) => (
            <div key={section.key} data-testid={`feed-section-${section.key}`}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-serif text-2xl font-semibold text-[#58111A] flex items-center gap-2">
                  {section.key === "recommended" && <Sparkles className="w-5 h-5 text-[#C59B27]" />} {section.title}
                </h2>
                <Link to="/app/matches" className="text-sm font-semibold text-[#781826] flex items-center gap-1">View all <ArrowRight className="w-4 h-4" /></Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {section.cards.slice(0, 4).map((p) => <MatchCard key={`${section.key}-${p.user_id}`} p={p} onChanged={() => api.get("/home-feed").then(({ data }) => setFeed(data.sections))} />)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-2xl font-semibold text-[#58111A] flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#C59B27]" /> Today's AI Matches</h2>
            <Link to="/app/matches" className="text-sm font-semibold text-[#781826] flex items-center gap-1">View all <ArrowRight className="w-4 h-4" /></Link>
          </div>
          {data.todays_matches?.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {data.todays_matches.map((p) => <MatchCard key={p.user_id} p={p} />)}
            </div>
          ) : (
            <Empty icon={Heart} title="No matches yet" message="Complete your profile and set your partner preferences to unlock relevant recommendations."
              action={<Link to="/app/profile/edit" className="btn-primary">Complete Profile</Link>} />
          )}
        </div>
      )}
    </div>
  );
}
